import Spinner from 'components/Spinner'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import { useIsAdminWithLoading } from 'hooks/useIsAdmin'
import React from 'react'
import { useSelector } from 'react-redux'
import { Redirect, Route, RouteProps } from 'react-router-dom'
import { selectCurrentMerchantUser } from 'selectors/merchantUser'
import { selectLegalDocsEnforced } from 'slices/legalDocsBlock'
import { useAbility } from 'utilities/ability'
import type { CaslSubject } from 'utilities/permissionMap'

type Props = {
  component: React.ComponentType<any>
  redirect: string
  resource: CaslSubject
  accessType?: 'manage' | 'access'
  // Gate on admin status instead of a CASL ability. Used for pages with no
  // dedicated backend permission resource (e.g. Integrations) — the nav and
  // route share the same useIsAdmin source of truth so the link and the
  // page can't disagree. NOTE: when adminOnly=true, the `resource` prop is
  // ignored at runtime (kept for log/telemetry consistency). If you later
  // add a real permission_resource for this page, drop adminOnly so the
  // CASL ability actually gates.
  adminOnly?: boolean
} & RouteProps

const ProtectedRoute: React.FC<Props> = ({
  component: Component,
  redirect,
  resource,
  accessType,
  adminOnly = false,
  ...routeParams
}) => {
  const merchant = useCurrentMerchant()
  const merchantUser = useSelector(selectCurrentMerchantUser)
  const legalDocsEnforced = useSelector(selectLegalDocsEnforced)
  const rbacEnabled = useFlag('role-based-access-control', false)
  // Called unconditionally to keep hooks-order stable. When adminOnly is
  // false, the result is discarded — but the underlying RBAC queries are
  // already subscribed by the nav (useRbacNavCheck and friends), so this
  // adds React subscription bookkeeping per protected-route render, not
  // any extra network. If RBAC route-gating becomes the common case we
  // can factor a thinner non-admin-only variant.
  const { isAdmin, isLoading: isAdminLoading } = useIsAdminWithLoading()

  // Wait for required data to load before checking abilities,
  // otherwise, refreshing the page will cause the user to ALWAYS
  // get redirected out of the route
  const isDataLoading =
    !merchant || !merchantUser || (adminOnly && isAdminLoading)

  // When RBAC is enabled, default to 'access' (view-level) instead of 'manage'.
  // Explicit accessType prop still takes precedence.
  const resolvedAccessType = accessType ?? (rbacEnabled ? 'access' : 'manage')
  const ability = useAbility()
  const hasAbility = adminOnly
    ? isAdmin
    : ability.can(resolvedAccessType, resource)

  return (
    <Route
      {...routeParams}
      render={props => {
        // When the backend's legal-docs-required block is active, identity
        // reads (merchant_users/me, merchants/:id) 403 — so the ORM-backed
        // data + ability checks below would never resolve. The user is an
        // admin (the backend only enforces against admins) and is locked to
        // the legal docs path by useLegalDocsBlock, so it's safe to render
        // through.
        if (legalDocsEnforced) {
          return <Component {...props} />
        }

        // Show loading spinner while data is loading
        if (isDataLoading) {
          return (
            <Spinner
              isLoading
              size="4x"
              className="text-center margin-top-huge"
            />
          )
        }

        return hasAbility ? (
          <Component {...props} />
        ) : (
          <Redirect to={redirect} />
        )
      }}
    />
  )
}

export default ProtectedRoute
